import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import "moment/locale/ja";
import "moment/locale/zh-cn";
import "moment/locale/ru";
import "moment/locale/uk";
import "moment/locale/it";
import "moment/locale/de";
import "moment/locale/fa";
import "moment/locale/ar";
import { LANGUAGE_SWITCHING } from "../config";

export const SUPPORTED_LANGUAGES = ["en", "es", "pt", "ja", "zh", "ru", "uk", "it", "de", "fa", "ar"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const I18N_NAMESPACES = [
  "common",
  "navigation",
  "settings",
  "auth",
  "home",
  "barracks",
  "research",
  "marketplace",
  "adventure",
  "guild",
  "profile",
  "treasure",
  "leaderboard",
  "notifications",
  "village",
  "battle",
  "learn",
  "dao",
  "referrals",
  "errors",
  "toasts",
  // The Events dialog: Wheel of Luck tabs, spins, rewards.
  "wheel",
] as const;

export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

// Where the chosen language is kept between visits.
export const LANGUAGE_STORAGE_KEY = "i18nextLng";

const normalizeLanguage = (lng?: string): SupportedLanguage => {
  if (!lng) return "en";
  const lowered = lng.toLowerCase();
  if (lowered.startsWith("es")) return "es";
  if (lowered.startsWith("pt")) return "pt";
  if (lowered.startsWith("ja")) return "ja";
  if (lowered.startsWith("zh")) return "zh";
  if (lowered.startsWith("ru")) return "ru";
  if (lowered.startsWith("uk")) return "uk";
  if (lowered.startsWith("ua")) return "uk";
  if (lowered.startsWith("it")) return "it";
  if (lowered.startsWith("de")) return "de";
  if (lowered.startsWith("fa")) return "fa";
  if (lowered.startsWith("ar")) return "ar";
  return "en";
};

const momentLocaleByLanguage: Record<SupportedLanguage, string> = {
  en: "en",
  es: "es",
  pt: "pt-br",
  ja: "ja",
  zh: "zh-cn",
  ru: "ru",
  uk: "uk",
  it: "it",
  de: "de",
  fa: "fa",
  ar: "ar",
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // English only where players cannot switch (the play build); see LANGUAGE_SWITCHING.
    ...(LANGUAGE_SWITCHING ? {} : { lng: "en" }),
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LANGUAGES],
    defaultNS: "common",
    ns: [...I18N_NAMESPACES],
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ["localStorage"],
      // A browser asks for "uk-UA, ru" or "de-DE, en": compare by language, or
      // the second one wins for being an exact match.
      convertDetectedLanguage: (lng: string) => lng.split(/[-_]/)[0],
    },
    react: {
      useSuspense: false,
    },
  });

const applyRuntimeLocale = (language?: string) => {
  const locale = normalizeLanguage(language);
  moment.locale(momentLocaleByLanguage[locale]);

  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === "fa" || locale === "ar" ? "rtl" : "ltr";
  }
};

applyRuntimeLocale(i18n.resolvedLanguage);
i18n.on("languageChanged", (lng) => {
  applyRuntimeLocale(lng);
});

if (process.env.NODE_ENV !== "production") {
  i18n.on("missingKey", (lngs, namespace, key) => {
    // Keeps development visibility without polluting production logs.
    // eslint-disable-next-line no-console
    console.warn("[i18n:missing-key]", { lngs, namespace, key });
  });
}

// localStorage.clear() that keeps the language the player chose.
export const clearStorageKeepingLanguage = () => {
  const language = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  localStorage.clear();
  if (language) localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};

export { normalizeLanguage };
export default i18n;
